<template>
    <ul class="flex flex-wrap flex-space-between module-data-container">
        <li class="module-data-item" v-for="module in station.modules" v-bind:key="module.id" @click="onModuleClick(module.id)">
            <h3 class="module-data-title">
                <img alt="Module icon" :src="getModuleImg(module)" />
                {{ getModuleName(module) }}
                <span class="module-bay-number" v-if="getModuleBay(module)">{{ getModuleBay(module) }}</span>
            </h3>
            <TinyChart
                :ref="'tinyChart-' + module.id"
                :moduleKey="getModuleKey(module)"
                :moduleHardwareId="module.hardwareId"
                :station-id="station.id"
                :station="station"
                :querier="sensorDataQuerier"
            />
        </li>
    </ul>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { DisplayModule, DisplayStation } from "@/store";
import * as utils from "@/utilities";
import TinyChart from "@/views/viz/TinyChart.vue";
import { BookmarkFactory, serializeBookmark, FastTime } from "@/views/viz/viz";
import { openExploreForSensor } from "@/views/viz/open-explore";
import { VizSensor } from "@/views/viz/api";
import { SensorDataQuerier } from "@/views/shared/sensor_data_querier";

export default Vue.extend({
    name: "StationModules",
    components: { TinyChart },
    props: {
        station: {
            type: Object as PropType<DisplayStation>,
            default: null,
        },
    },
    data(): {
        sensorDataQuerier: SensorDataQuerier;
    } {
        return {
            sensorDataQuerier: new SensorDataQuerier(this.$services.api),
        };
    },
    methods: {
        getModuleImg(module: DisplayModule): string {
            return this.$loadAsset(utils.getModuleImg(module));
        },
        getModuleName(module: DisplayModule): string {
            return module.label || this.$tc(module.name.replace("modules.", "fk."));
        },
        getModuleKey(module: DisplayModule): string {
            return utils.getModuleKey(module);
        },
        getModuleBay(module: DisplayModule): string | null {
            return module.bay ? `Bay ${module.bay}` : null;
        },
        async onModuleClick(moduleId: number) {
            const tinyChartComp = this.$refs["tinyChart-" + moduleId];
            if (tinyChartComp && tinyChartComp[0]) {
                const vizData = tinyChartComp[0].vizData;

                if (vizData) {
                    await openExploreForSensor(this.$services.api, this.$router, this.station.id, vizData.vizSensor, vizData.timeRange);
                    return;
                }

                await this.openExploreFallback(moduleId);
            }
        },
        async openExploreFallback(moduleId: number): Promise<void> {
            const twoDays = 2 * 86400000;
            const newWindow = window.open("about:blank", "_blank");
            if (!newWindow) {
                console.error("Failed to open new window - popup may be blocked");
                return;
            }

            try {
                let bm;
                const module = this.station.modules.find((m) => m.id === moduleId);
                const moduleKey = module ? this.getModuleKey(module) : null;

                if (module && moduleKey) {
                    const [quickSensorsResponse, tailData] = await Promise.all([
                        this.$services.api.getQuickSensors([this.station.id]),
                        this.$services.api.tailSensorData([this.station.id]),
                    ]);
                    const quickSensors = quickSensorsResponse.stations[this.station.id];
                    const matchingSensors = quickSensors.filter((qs) => qs.moduleId === module.hardwareId);
                    const sensorWithData = matchingSensors.find((sensor) =>
                        tailData.data.some((row) => row.sensorId === sensor.sensorId && row.moduleId === sensor.moduleId)
                    );

                    if (sensorWithData) {
                        const sensorData = tailData.data.filter(
                            (row) => row.sensorId === sensorWithData.sensorId && row.moduleId === sensorWithData.moduleId
                        );
                        const vizSensor: VizSensor = [this.station.id, [sensorWithData.moduleId, sensorWithData.sensorId]];
                        const lastReading = Math.max(...sensorData.map((row) => row.time));
                        bm = BookmarkFactory.forSensor(
                            this.station.id,
                            vizSensor,
                            [lastReading - twoDays, lastReading],
                            FastTime.FortyEightHours
                        );
                    } else if (matchingSensors.length > 0) {
                        const sensor = matchingSensors[0];
                        const vizSensor: VizSensor = [this.station.id, [sensor.moduleId, sensor.sensorId]];
                        const recently = await this.$services.api.queryStationsRecently([this.station.id]);
                        const lastTimestamp = recently.stations[this.station.id]?.last;
                        const endTime = lastTimestamp || Date.now();
                        bm = BookmarkFactory.forSensor(this.station.id, vizSensor, [endTime - twoDays, endTime], FastTime.FortyEightHours);
                    } else {
                        bm = BookmarkFactory.forStation(this.station.id);
                    }
                } else {
                    bm = BookmarkFactory.forStation(this.station.id);
                }

                const encoded = serializeBookmark(bm);
                const savedBookmark = await this.$services.api.saveBookmark(encoded);
                const url = this.$router.resolve({
                    name: "exploreShortBookmark",
                    query: { v: savedBookmark.token },
                }).href;
                newWindow.location.href = url;
            } catch (error) {
                newWindow.close();
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.module-data-container {
    gap: 20px;

    @include mixins.bp-down(variables.$sm) {
        gap: 10px;
    }
}

.module-data-item {
    flex: 1 1 calc(50% - 10px);
    min-width: 0;
    z-index: variables.$z-index-top;

    @include mixins.bp-down(variables.$sm) {
        flex: 0 0 100%;
    }
}

.module-data-title {
    color: variables.$color-primary;
    font-size: 12px;
    margin-bottom: 10px;
    cursor: pointer;
    margin-top: 0;
    display: flex;
    align-items: flex-end;

    img {
        margin-right: 7px;
        width: 19px;
        height: 19px;
    }

    .module-bay-number {
        margin-left: 8px;
        transform: translateY(-1px);
    }
}
</style>
